<template>
  <v-container fluid class="pa-0">
    <!-- table -->
    <div class="table table-hover">
      <div class="table-subheader has-border-b">{{ $t(`${namespace}.openInvoice`) }}</div>

      <!-- table header -->
      <v-layout
        align-center
        class="grey--text text--darken-4 px-3 py-1 caption grey lighten-4 has-border-b table-header"
      >
        <v-flex class="table__checkbox table__td--prevent" xs1 />
        <v-flex xs1># {{ $t('Common.note') }}</v-flex>
        <v-flex xs2>Status</v-flex>
        <v-flex xs1>{{ $t('Common.maturity') }}</v-flex>
        <v-flex xs2>{{ $t('Common.period') }}</v-flex>
        <v-flex xs1>{{ $t('Common.ridesNumber') }}</v-flex>
        <v-flex xs2>{{ $t('Common.amount') }}</v-flex>
        <v-flex xs2>{{ $t(`${namespace}.billingType`) }}</v-flex>
      </v-layout>

      <div v-if="!company" class="table__alert">
        {{ $t(`${namespace}.openInvoiceMessage`) }}
      </div>

      <div v-if="openInvoice && openInvoice.id">
        <v-list subheader two-line class="table-body">
          <div class="open-invoice">
            <v-list-tile
              :class="{ 'active': selectedLine === 'row-open-invoice' }"
              @click="showDetails($event, 'row-open-invoice', 'open-invoice', openInvoice)"
              ref="row-open-invoice"
            >
              <v-list-tile-content>
                <v-list-tile-sub-title>
                  <v-layout align-center class="gray-7--text">
                    <v-flex class="table__checkbox table__td--prevent" xs1>
                      <v-tooltip
                        top
                        slot="activator"
                        max-width="175px"
                      >
                        <v-btn
                          v-if="hasRole('profiles', 'ROLE_ADMINISTRATOR')"
                          :to="{ name: 'FinancialAdjustments' }"
                          slot="activator"
                          flat
                          icon
                          class="dropdown-actions__activator gray-6--text mx-0"
                          @click.prevent
                        >
                          <v-icon color="gray-6">local_atm</v-icon>
                        </v-btn>
                        <span>{{ $t(`${namespace}.financialAdjustmentsTooltip`) }}</span>
                      </v-tooltip>
                    </v-flex>

                    <v-flex xs1 />

                    <v-flex xs2 />

                    <v-flex xs1>
                      <span v-if="openInvoice.id">
                        <span :title="openInvoice.id">{{ openInvoice.id }}</span>
                      </span>
                    </v-flex>

                    <v-flex xs2>
                      <span :title="$t(`${namespace}.currentMonth`)" class="text--ellipsis text-capitalize">
                        {{ $t(`${namespace}.currentMonth`) }}
                      </span>
                    </v-flex>

                    <v-flex xs1>
                      <span v-if="openInvoice.rides && openInvoice.rides.count">
                        <span :title="openInvoice.rides.count | formatNumber">
                          {{ openInvoice.rides.count | formatNumber }}
                        </span>
                      </span>
                    </v-flex>

                    <v-flex xs2>
                      <span v-if="openInvoice.total">
                        <span :title="openInvoice.total | currencyBRL">
                          {{ openInvoice.total | currencyBRL }}
                        </span>
                      </span>
                    </v-flex>

                    <v-flex xs2 />
                  </v-layout>
                </v-list-tile-sub-title>
              </v-list-tile-content>
            </v-list-tile>

            <v-divider />
          </div>
        </v-list>
      </div>

      <div v-if="closedInvoices">
        <div class="table-subheader has-border-b">{{ $t(`${namespace}.invoiceHistory`) }}</div>

        <v-list v-if="closedInvoices && closedInvoices.length" subheader two-line class="table-body">
          <div v-for="(item, i) in closedInvoices" :key="i" class="closed-invoice">
            <div :ref="`collapsed-${i}`">
              <v-list-tile
                :class="{ 'active': selectedLine === `row-${i}` }"
                @click="showDetails($event, `row-${i}`, 'invoices-history', item)" :ref="`row-${i}`"
              >
                <v-list-tile-content>
                  <v-list-tile-sub-title>
                    <v-layout align-center class="gray-7--text">
                      <v-flex class="table__checkbox table__td--prevent" xs1>
                        <FinancialDropdownActions
                          :status="item.paymentStatus"
                          :files="hasRole('financial', 'ROLE_GET_ORDER_DOCUMENTS_URL') ? item.documents : {}"
                          :icon="item.paymentStatus === 'AUTHORISED' || item.paymentStatus === 'INVOICE_SENT' || item.paymentStatus === 'LATE' || item.paymentStatus === 'EXTEND'
                            ? 'more_vert'
                            : null
                          "
                          :invoice="item"
                          tooltip
                          :tooltipText="getTooltipText(item.paymentStatus)"
                          right
                        />
                      </v-flex>

                      <v-flex class="text--ellipsis" xs1>
                        <span :title="item.numericalId" contenteditable>{{ item.numericalId }}</span>
                      </v-flex>

                      <v-flex class="text--ellipsis" xs2>
                        <span
                          v-if="item.paymentStatus"
                          class="bullet"
                          :class="[
                            { 'bullet--success': item.paymentStatus === 'PAID_OUT' },
                            { 'bullet--success-mix-warning': item.paymentStatus === 'INVOICE_SENT' || item.paymentStatus === 'AUTHORISED' },
                            { 'bullet--warning': item.paymentStatus === 'REFUSED' || item.paymentStatus === 'LATE' },
                            { 'bullet--success-mix-warning--reverse': item.paymentStatus === 'EXTEND' }
                          ]"
                        >
                          <span v-if="item.paymentStatus === 'PAID_OUT'">{{ $t(`${namespace}.paidOut`) }}</span>
                          <span v-else-if="item.paymentStatus === 'AUTHORISED'">{{ $t(`${namespace}.authorised`) }}</span>
                          <span v-else-if="item.paymentStatus === 'INVOICE_SENT'">{{ $t(`${namespace}.invoiceSent`) }}</span>
                          <span v-else-if="item.paymentStatus === 'REFUSED'">{{ $t(`${namespace}.refused`) }}</span>
                          <span v-else-if="item.paymentStatus === 'LATE'">{{ $t(`${namespace}.late`) }}</span>
                          <span v-else-if="item.paymentStatus === 'EXTEND'">{{ $t(`${namespace}.maturityExtended`) }}</span>
                          <span v-else>-</span>
                        </span>
                      </v-flex>

                      <v-flex class="invoice-history-list__span" xs1>
                        <span
                          class="invoice-history-list__span"
                          :class="{ 'font-weight--semibold': item.paymentStatus === 'INVOICE_SENT' || item.paymentStatus === 'LATE' || item.paymentStatus === 'EXTEND' }"
                          :title="`${item.dueDateOpt ? moment(item.dueDateOpt).format('DD/MM/YY') : '-'}`"
                        >
                          <span
                            v-if="isValidArray(item.oldInvoices)"
                            class="invoice-history-list"
                            @click="handleShowInvoiceHistory(`collapsed-${i}`)"
                          >
                            <v-icon class="collapsed-line__chevron_right">chevron_right</v-icon>
                            <v-icon class="collapsed-line__expand_more" color="accent">expand_more</v-icon>

                            <span v-if="item.paymentStatus === 'EXTEND'" class="invoice-history-list__span">
                              {{
                                item.newOrder.dueDate
                                  ? moment(item.newOrder.dueDate).format('DD/MM/YY')
                                  : '-'
                              }}
                            </span>

                            <span class="invoice-history-list__span" v-else>
                              {{
                                item.dueDateOpt
                                  ? moment(item.dueDateOpt).format('DD/MM/YY')
                                  : '-'
                              }}
                            </span>
                          </span>

                          <span v-else>
                            {{
                              item.dueDateOpt
                                ? moment(item.dueDateOpt).format('DD/MM/YY')
                                : '-'
                            }}
                          </span>
                        </span>
                      </v-flex>

                      <v-flex xs2>
                        <span :title="`${moment(item.periodStartDate).format('DD/MM/YY')} ${$t('Common.to')} ${item.executionDateOpt ? moment(item.executionDateOpt).subtract(1, 'd').format('DD/MM/YY') : '-'}
                        `">{{ moment(item.periodStartDate).format('DD/MM/YY') }}
                        {{ $t('Common.to') }}
                        {{
                          item.executionDateOpt
                            ? moment(item.executionDateOpt).subtract(1, 'd').format('DD/MM/YY')
                            : '-'
                        }}</span>
                      </v-flex>

                      <v-flex class="text--ellipsis" xs1>
                        <span :title="item.totalOfTaxiJobs">{{ item.totalOfTaxiJobs | formatNumber }}</span>
                      </v-flex>

                      <v-flex xs2>
                        <span
                          :class="{ 'font-weight--semibold': item.paymentStatus === 'INVOICE_SENT' || item.paymentStatus === 'LATE' || item.paymentStatus === 'EXTEND' }"
                          :title="item.totalValue | currencyBRL"
                        >
                          <span v-if="item.paymentStatus === 'EXTEND'">{{ item.newOrder.amount | currencyBRL }}</span>
                          <span v-else>{{ item.totalValue | currencyBRL }}</span>
                        </span>
                      </v-flex>

                      <v-flex xs2>
                        <span :title="getBilletType(item)">{{ getBilletType(item) }}</span>
                      </v-flex>
                    </v-layout>
                  </v-list-tile-sub-title>
                </v-list-tile-content>
              </v-list-tile>

              <v-divider />

              <div v-if="isValidArray(item.oldInvoices)" class="collapsed-line">
                <div v-for="oldInvoice in item.oldInvoices" :key="oldInvoice.duplicateRequestId">
                  <v-list-tile>
                    <v-list-tile-content>
                      <v-list-tile-sub-title>
                        <v-layout align-center class="gray-7--text">
                          <v-flex class="table__checkbox table__td--prevent" xs1 />

                          <v-flex class="text--ellipsis line-through" xs1 />

                          <v-flex class="text--ellipsis text-capitalize" xs2>
                            <span class="bullet bullet--canceled">
                              <span>{{ $t(`${namespace}.canceled`) }}</span>
                            </span>
                          </v-flex>

                          <v-flex xs1>
                            <span class="line-through">{{ oldInvoice.dueDate | formatDate('DD/MM/YY') }}</span>
                          </v-flex>

                          <v-flex xs2>
                            <span :title="`${moment(item.periodStartDate).format('DD/MM/YY')} ${$t('Common.to')} ${item.executionDateOpt ? moment(item.executionDateOpt).subtract(1, 'd').format('DD/MM/YY') : '-'}
                            `">{{ moment(item.periodStartDate).format('DD/MM/YY') }}
                            {{ $t('Common.to') }}
                            {{
                              item.executionDateOpt
                                ? moment(item.executionDateOpt).subtract(1, 'd').format('DD/MM/YY')
                                : '-'
                            }}</span>
                          </v-flex>

                          <v-flex class="text--ellipsis" xs1>
                            <span :title="item.totalOfTaxiJobs">{{ item.totalOfTaxiJobs | formatNumber }}</span>
                          </v-flex>

                          <v-flex xs2>
                            <span class="line-through">{{ oldInvoice.amount | currencyBRL }}</span>
                          </v-flex>

                          <v-flex xs2>
                            <span :title="getBilletType(item)">{{ getBilletType(item) }}</span>
                          </v-flex>
                        </v-layout>
                      </v-list-tile-sub-title>
                    </v-list-tile-content>
                  </v-list-tile>

                  <v-divider />
                </div>
              </div>
            </div>
          </div>
        </v-list>
      </div>
    </div>

    <!-- pagination -->
    <div v-if="isSuccess && itemsCounter > limit" class="text-xs-center">
      <v-container class="px-0">
        <v-pagination
          v-model="pageIncrement"
          color="accent"
          :total-visible="totalVisible"
          :length="length"
          @input="goToPage"
        />
      </v-container>
    </div>

    <!-- loading data -->
    <div v-if="isLoading" class="text-xs-center">
      <v-progress-circular class="my-5" indeterminate color="accent" :size="50" />
    </div>

    <!-- none found -->
    <EmptyState
      v-if="isSuccess && (!closedInvoices || !closedInvoices.length)"
      :text="$t('Common.emptyFinancial')"
      class="py-5"
    />

    <!-- error -->
    <div v-if="isFailed" class="text-xs-center py-5">
      <span>
        {{ $t('Common.genericErrorMsg') }}<br>
        <a
          href=""
          :title="$t('Common.tryAgain')"
          class="text--link accent--text"
          @click.prevent="getInvoices"
        >
          {{ $t('Common.tryAgain') }}.
        </a>
      </span>
    </div>

    <GlobalDrawer
      :show="globalDrawer.visibility"
      :drawerType="globalDrawer.type"
      :data="globalDrawer.data"
      :filters="globalDrawer.filters"
      @close="closeGlobalDrawer"
    />
  </v-container>
</template>

<script>
import moment from 'moment'
import { mapState } from 'vuex'
import { STATUS_IS_LOADING, STATUS_SUCCESS, STATUS_FAILED } from 'utils/constants'
import { isArray, isObject, objectToArray, hasRole } from 'utils/functions'
import EmptyState from 'components/EmptyState'
import FinancialDropdownActions from './FinancialDropdownActions'
import GlobalDrawer from 'components/globalDrawer'

export default {
  name: 'FinancialInvoicesHistory',
  components: {
    EmptyState,
    FinancialDropdownActions,
    GlobalDrawer
  },
  computed: {
    ...mapState({
      company: state => state.company.selectedCompany
    }),

    totalVisible () {
      return window.innerWidth <= 420 ? 5 : 7
    },

    isLoading () {
      return this.currentStatus === STATUS_IS_LOADING
    },

    isSuccess () {
      return this.currentStatus === STATUS_SUCCESS
    },

    isFailed () {
      return this.currentStatus === STATUS_FAILED
    }
  },
  data () {
    return {
      namespace: 'Components.FinancialInvoicesHistory',
      items: null,
      openInvoice: null,
      closedInvoices: null,
      limit: 25,
      page: 0,
      pageIncrement: 1,
      length: 1,
      itemsCounter: 0,
      currentStatus: null,
      selectedRow: null, // verificar
      selectedLine: null,
      selectedInvoice: {},
      dialog: false,
      refundId: null,
      filters: {
        hasFilter: false,
        startDate: null,
        endDate: null
      },
      globalDrawer: {
        visibility: false,
        type: '',
        data: {},
        filters: {}
      }
    }
  },
  methods: {
    hasRole (group, role) {
      return hasRole(group, role)
    },

    isValidArray (val) {
      return isArray(val)
    },

    isValidObject (val) {
      return isObject(val)
    },

    getTooltipText (paymentStatus) {
      if (paymentStatus === 'AUTHORISED' || paymentStatus === 'INVOICE_SENT') {
        return this.$t(`${this.namespace}.tooltipTextInvoiceSent`)
      }

      if (paymentStatus === 'LATE') {
        return this.$t(`${this.namespace}.tooltipTextLate`)
      }

      if (paymentStatus === 'EXTEND') {
        return this.$t(`${this.namespace}.tooltipTextExtended`)
      }

      return null
    },

    showDetails ($event, ref, kind = null, data = null) {
      const target = $event.target.classList

      if (
        !target.contains('table-actions') &&
        !target.contains('material-icons') &&
        !target.contains('dropdown-actions__activator') &&
        !target.contains('invoice-history-list') &&
        !target.contains('invoice-history-list__span')
      ) {
        this.openGlobalDrawer({ data })
        this.selectedLine = ref
        this.selectedRow = data
      }
    },

    // OPEN INVOICES
    async getOpenInvoices () {
      if (!this.hasRole('billing', 'ROLE_GET_ORDER_PREVIEW_SUMMARY')) return

      this.openInvoice = null

      try {
        const { data: res } = await this.$apiService.getOpenInvoice(this.company.id)

        if (!res.valid) {
          const error = this.isValidArray(res.reasons) ? res.reasons[0] : res.message
          throw new Error(error)
        }

        if (res && res.content) {
          const { ridesAmount, ridesCount } = res.content

          this.openInvoice = {
            id: null,
            companyName: this.company.displayName,
            totalOfTaxiJobs: ridesCount,
            totalValue: ridesAmount,
            ...res.content
          }
        }
      } catch (err) {
        console.log('ERROR', err)
      }
    },

    // CLOSED INVOICES
    async getInvoices () {
      if (!this.hasRole('billing', 'ROLE_GET_ORDERS')) return

      this.currentStatus = STATUS_IS_LOADING

      // reset fields
      this.closedInvoices = []
      this.length = 1
      this.itemsCounter = 0

      try {
        const company = this.company ? this.company.id : null
        const startDate = this.filters.startDate ? this.filters.startDate : null
        const endDate = this.filters.endDate ? this.filters.endDate : null
        const { data: res } = await this.$apiService.getRefundsList(
          { limit: this.limit },
          { page: this.page },
          { orderBy: 'desc' },
          { company },
          { startDate },
          { endDate }
        )

        if (!res.valid) {
          this.currentStatus = STATUS_FAILED
          throw new Error(res.message)
        }

        if (res && this.isValidArray(res.content)) {
          this.closedInvoices = res.content.map(item => {
            const oldInvoicesArray = item.oldInvoices ? objectToArray(item.oldInvoices).reverse() : []
            const oldInvoices = oldInvoicesArray.filter((item, index) => index >= 0)
            const newOrder = this.isValidArray(oldInvoicesArray) ? oldInvoicesArray[0] : null
            const diffDays = newOrder && newOrder.dueDate
              ? Math.ceil(newOrder.dueDate && moment(newOrder.dueDate).diff(moment(), 'days', true))
              : -1
            const paymentStatus = newOrder && newOrder.dueDate && diffDays >= 0
              ? 'EXTEND'
              : item.paymentStatus

            return {
              ...item,
              paymentStatus,
              oldInvoices,
              newOrder
            }
          })

          this.length = res.totalPages
          this.itemsCounter = res.totalElements
        } else {
          this.closedInvoices = []
        }

        this.currentStatus = STATUS_SUCCESS
      } catch (err) {
        console.log('Error', err)
        this.currentStatus = STATUS_FAILED
      }
    },

    openDialog (refundId) {
      this.refundId = refundId
      this.dialog = true
    },

    closeDialog () {
      this.dialog = false
      this.selectedInvoice = {}
    },

    goToPage () {
      this.page = this.pageIncrement - 1
      this.getInvoices()
    },

    openGlobalDrawer ({ data = {}, filters = {} }) {
      this.globalDrawer = {
        visibility: true,
        type: 'financial',
        data,
        filters
      }
    },

    closeGlobalDrawer () {
      this.selectedRow = null
      this.selectedLine = null
      this.selectedInvoice = {}

      this.globalDrawer = {
        visibility: false,
        type: '',
        data: {},
        filters: {}
      }
    },

    getBilletType (item) {
      const RIDE = Boolean(item.contractReceiptTypes.find(item => item === 'RIDE'))

      if (item.contractReceiptTypes.length === 1 && RIDE) {
        return 'Boleto de corrida'
      }

      if (item.contractReceiptTypes.length >= 1 && !RIDE) {
        return 'Boleto serviço'
      }

      if (item.contractReceiptTypes.length && RIDE) {
        return 'Boleto unificado'
      }
    },

    resetPagination () {
      this.limit = 25
      this.page = 0
      this.pageIncrement = 1
      this.length = 1
      this.itemsCounter = 0
    },

    handleShowInvoiceHistory (collapsed) {
      const targetLine = this.$refs[collapsed][0]
      targetLine.classList.toggle('show-collapsed-lines')
    }
  },
  watch: {
    filters: {
      deep: true,
      handler () {
        this.getInvoices()
        this.getOpenInvoices()
        this.closeGlobalDrawer()
      }
    },

    company () {
      this.resetPagination()
      this.getInvoices()
      this.getOpenInvoices()
      this.closeGlobalDrawer()
    }
  },
  async mounted () {
    await Promise.all([
      this.getInvoices(),
      this.getOpenInvoices()
    ])
  }
}
</script>

<style lang="scss">
.table-subheader {
  font-weight: 600;
  padding: 12px 16px;
}

.table-hover .theme--light.v-list .v-list__tile:hover {
  background: rgba(0,0,0,0.04);
}

.table-hover .table-body.v-list .open-invoice [role="listitem"] .v-list__tile {
  background-color: #f3fef7;
}

.table-hover .table-body.v-list .open-invoice [role="listitem"].active .v-list__tile {
  background-color: #bbfbd6;
}

.table-hover .table-body.v-list .closed-invoice [role="listitem"].active .v-list__tile,
.table-hover .table-body.v-list .closed-invoice [role="listitem"].active .v-list__tile:hover {
  background-color: #fffadd;
}

.table-hover .theme--light.v-list  {

  .collapsed-line {
    background-color: $gray-1;

    .v-list__tile:hover {
      background-color: $gray-1;
    }

    .v-list__tile__content {
      cursor: default;
    }
  }
}

.collapsed-line__chevron_right,
.collapsed-line__expand_more {
  line-height: 0.7;
  width: 10px;
}

.collapsed-line,
.collapsed-line__expand_more {
  display: none;
}

.collapsed-line__chevron_right {
  display: inline-flex;
}

.show-collapsed-lines {

  .collapsed-line__expand_more {
    display: inline-flex;
  }

  .collapsed-line__chevron_right {
    display: none;
  }

  .collapsed-line {
    display: block;;
  }
}
</style>
